import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import * as Sentry from '@sentry/react';
import { getIdToken } from './authentication';
import { parseBillingAccessErrorMessage, redirectToBillingAccess } from './billing-access';

function toJson(value: unknown) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

const httpLink = createHttpLink({
  uri: import.meta.env.APPSYNC_URL,
});

const apolloAuthLink = setContext(async (_, { headers }) => {
  const jwtToken = await getIdToken();

  return {
    headers: {
      ...headers,
      authorization: jwtToken ? `Bearer ${jwtToken}` : ``,
      // 'X-Api-Key': import.meta.env.APPSYNC_KEY,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, response }) => {
  if (graphQLErrors)
    graphQLErrors.forEach((graphQLError) => {
      const message =
        graphQLError?.message ||
        (graphQLError?.extensions as { originalError?: { errorMessage?: string } } | undefined)?.originalError
          ?.errorMessage ||
        'Unknown GraphQL error';

      const locations = graphQLError?.locations?.map((loc) => JSON.stringify(loc)).join();
      const path = graphQLError?.path?.join();

      const msg = `Apollo: [GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Operation: ${operation.operationName || 'unnamed'}`;

      const billingError = parseBillingAccessErrorMessage(message);
      if (billingError) {
        redirectToBillingAccess(billingError);
      }

      console.error(msg, {
        graphQLError,
        extensions: graphQLError?.extensions,
        response,
        responseErrorsJson: toJson(response?.errors),
      });
      Sentry.captureMessage(msg, {
        level: 'error',
        extra: {
          graphQLError,
          extensions: graphQLError?.extensions,
          response,
          responseErrorsJson: toJson(response?.errors),
          operationName: operation.operationName,
        },
      });
    });
  if (networkError) {
    Sentry.captureMessage(`Apollo: [Network error]: ${networkError.toString()}`, {
      level: 'error',
      extra: { networkError, operationName: operation.operationName },
    });

    console.error(`[Network error]: ${networkError.toString()}`, {
      networkError,
      operationName: operation.operationName,
    });
  }
});

const client = new ApolloClient({
  link: from([errorLink, apolloAuthLink.concat(httpLink)]),
  cache: new InMemoryCache(),
});

export { client };
